import { ASPECT_RATIOS, aspectValue, type AspectRatio } from '@panels/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, createRoom } from '../lib/api.js';
import { ErrorNote, Screen } from '../components/ui.js';

const LABELS: Record<AspectRatio, string> = {
  '1:2': 'Paper',
  '3:4': 'Tall',
  '1:1': 'Square',
};

/** Aspect ratio is the only thing to configure, and it locks once a round starts. */
export function CreateRoom() {
  const navigate = useNavigate();
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:2');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const state = await createRoom({ aspectRatio });
      navigate(`/room/${state.room.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create a room');
      setBusy(false);
    }
  };

  return (
    <Screen title="New room" back="/">
      <p className="muted">Pick a panel shape. Everyone in the room draws on this shape.</p>

      <div className="ratio-picker">
        {ASPECT_RATIOS.map((ratio) => (
          <button
            key={ratio}
            type="button"
            className={`ratio-option ${ratio === aspectRatio ? 'selected' : ''}`}
            onClick={() => setAspectRatio(ratio)}
            aria-pressed={ratio === aspectRatio}
          >
            <span className="ratio-thumbs" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span key={i} className="ratio-thumb" style={{ width: 26 * aspectValue(ratio), height: 26 }} />
              ))}
            </span>
            <span className="ratio-name">{LABELS[ratio]}</span>
            <span className="ratio-value">{ratio}</span>
          </button>
        ))}
      </div>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <button type="button" className="button primary" onClick={create} disabled={busy}>
        {busy ? 'Creating…' : 'Create room'}
      </button>
    </Screen>
  );
}
